/**
 * The activity log's vocabulary (client, 2026-10-03).
 *
 * The client asked for one log of everything anybody does on the platform — members,
 * professionals, the team, guest instructors and the platform itself — that the desk can
 * narrow by person, by kind of person and by area, to find out what happened when
 * something goes wrong. Every entry is a row of `audit_log`; these are the words for it.
 *
 * **Every action that can be written is named here, with its area.** The API checks it
 * both ways (`test/activity.test.ts`): a route or a job that records an action with no
 * words here fails the build, rather than appearing on the desk as `provider.status`.
 * Written out rather than derived from the identifier, because "Class Comment Removed"
 * is grammar, not language.
 *
 * The words are written as what the person did — the desk puts the name in front:
 * "Riley Morgan · Booked a free introductory call".
 */
/** Which part of the product an entry is about. The desk's Area filter. */
export const ACTIVITY_AREAS = [
    'accounts',
    'bookings',
    'classes',
    'events',
    'payments',
    'listings',
    'support',
    'team',
    'settings',
    'health',
    'platform',
];
export const ACTIVITY_AREA_WORDS = {
    accounts: 'Accounts and sign-in',
    bookings: 'Appointments',
    classes: 'Classes',
    events: 'Events',
    payments: 'Payments',
    listings: 'Professionals’ listings',
    support: 'Support and reports',
    team: 'The team',
    settings: 'Settings and content',
    health: 'Health records',
    platform: 'The platform',
};
/**
 * Who acted. `visitor` is somebody not signed in — a failed sign-in, a website form;
 * `system` is the platform itself — a card payment settling, a nightly sweep.
 */
export const ACTOR_KINDS = ['member', 'professional', 'team', 'instructor', 'system', 'visitor'];
export const ACTOR_KIND_WORDS = {
    member: 'Member',
    professional: 'Professional',
    team: 'Team',
    instructor: 'Guest instructor',
    system: 'The platform',
    visitor: 'Not signed in',
};
/** Where it was done. `web` is a browser that is not the desk: the professional's sign-up, the website. */
export const ACTIVITY_SOURCES = ['app', 'desk', 'web', 'system'];
export const ACTIVITY_SOURCE_WORDS = {
    app: 'The app',
    desk: 'The desk',
    web: 'The web',
    system: 'Automatic',
};
export const ACTIVITY_OUTCOMES = ['ok', 'failed'];
export const ACTIVITY_OUTCOME_WORDS = {
    ok: 'Done',
    failed: 'Did not work',
};
const a = (words, area) => ({ words, area });
/**
 * Every action, in words, and the area it files under.
 *
 * Kept in one list sorted by identifier so a missing one is easy to see. An action that
 * is no longer written stays, so the entries already in the log still read as words.
 */
export const ACTIVITY = {
    // --- accounts and sign-in ---------------------------------------------------------
    'account.accepted_terms': a('Accepted the terms', 'accounts'),
    'account.blocked': a('Blocked somebody', 'support'),
    'account.city_changed': a('Changed their city', 'accounts'),
    'account.closed': a('Account closed and personal details removed', 'accounts'),
    'account.closure_cancelled': a('Kept their account after asking to close it', 'accounts'),
    'account.closure_requested': a('Asked to close their account', 'accounts'),
    'account.code_resent': a('Asked for a new sign-up code', 'accounts'),
    'account.details_changed': a('Changed their name or details', 'accounts'),
    'account.export_downloaded': a('Downloaded a copy of their data', 'accounts'),
    'account.export_requested': a('Asked for a copy of their data', 'accounts'),
    'account.notification_settings': a('Changed their notification settings', 'accounts'),
    'account.password_reset': a('Set a new password from a reset link', 'accounts'),
    'account.password_reset_requested': a('Asked for a password reset link', 'accounts'),
    'account.phone_changed': a('Changed the phone number on a new account', 'accounts'),
    'account.photo_changed': a('Changed their photo', 'accounts'),
    'account.sign_in_failed': a('Tried to sign in', 'accounts'),
    'account.signed_in': a('Signed in', 'accounts'),
    'account.signed_out': a('Signed out', 'accounts'),
    'account.signed_up': a('Created an account', 'accounts'),
    'account.suspended_for_no_shows': a('Suspended for missing too many appointments', 'accounts'),
    'account.unblocked': a('Unblocked somebody', 'support'),
    'account.verified': a('Confirmed their sign-up code', 'accounts'),
    'user.hard_delete': a('Deleted a member for good', 'accounts'),
    'user.suspension': a('Suspended or restored a member', 'accounts'),
    // --- the team -----------------------------------------------------------------------
    'admin.changed_own_password': a('Changed their own password', 'team'),
    'admin.mfa_enrolled': a('Set up their authenticator app', 'team'),
    'admin.mfa_failed': a('Entered a wrong authenticator code', 'team'),
    'admin.mfa_reset_requested': a('Asked to reset their authenticator app', 'team'),
    'admin.mfa_reset_self': a('Reset their own authenticator app', 'team'),
    'admin.renamed_self': a('Changed their own name', 'team'),
    'admin.signed_in': a('Signed in to the desk', 'team'),
    'admin_role.created': a('Created a role', 'team'),
    'admin_role.deleted': a('Deleted a role', 'team'),
    'admin_role.updated': a('Changed what a role may do', 'team'),
    'team.added': a('Gave somebody access to the desk', 'team'),
    'team.invitation_accepted': a('Accepted an invitation to the desk', 'team'),
    'team.mfa_reset': a('Reset a colleague’s authenticator app', 'team'),
    'team.removed': a('Took away somebody’s access', 'team'),
    'team.role_changed': a('Changed somebody’s role', 'team'),
    // --- appointments -------------------------------------------------------------------
    'booking.availability_alert': a('Asked to hear when a professional has time', 'bookings'),
    'booking.booked': a('Booked a free introductory call', 'bookings'),
    'booking.cancelled': a('Cancelled an appointment', 'bookings'),
    'booking.cancelled_by_admin': a('Called off an appointment', 'bookings'),
    'booking.cancelled_by_professional': a('Cancelled an appointment with a client', 'bookings'),
    'booking.completed': a('Marked an appointment as done', 'bookings'),
    'booking.completed_automatically': a('Appointment marked as done after it ended', 'bookings'),
    'booking.expired': a('Unpaid appointment let go', 'bookings'),
    'booking.moved': a('Moved an appointment', 'bookings'),
    'booking.no_show': a('Marked a client as not having come', 'bookings'),
    'booking.time_held': a('Held a time to book', 'bookings'),
    'booking.time_released': a('Let go of a held time', 'bookings'),
    // --- classes ------------------------------------------------------------------------
    'class.cancel': a('Called off a class', 'classes'),
    'class.comment_removed': a('Removed a comment', 'classes'),
    'class.comment_restored': a('Put a comment back', 'classes'),
    'class.commented': a('Commented in a class', 'classes'),
    'class.create': a('Created a class', 'classes'),
    'class.end': a('Ended a class', 'classes'),
    'class.enrolled': a('Signed up for a class', 'classes'),
    'class.host_joined': a('Joined a class as the host', 'classes'),
    'class.instructor_confirmed': a('Confirmed their details as a guest instructor', 'classes'),
    'class.instructor_invited': a('Invited a guest instructor', 'classes'),
    'class.instructor_opened_link': a('Opened their guest instructor link', 'classes'),
    'class.instructor_revoked': a('Took back a guest instructor’s link', 'classes'),
    'class.instructor_set': a('Set who teaches a class', 'classes'),
    'class.joined': a('Joined a class', 'classes'),
    'class.left': a('Left a class', 'classes'),
    'class.material_added': a('Added a handout to a class', 'classes'),
    'class.material_removed': a('Removed a handout from a class', 'classes'),
    'class.participant_muted': a('Muted somebody in a class', 'classes'),
    'class.participant_unmuted': a('Unmuted somebody in a class', 'classes'),
    'class.place_given_back': a('Gave back their place in a class', 'classes'),
    'class.publish': a('Published a class', 'classes'),
    'class.question_added': a('Added a question to a class', 'classes'),
    'class.question_answered': a('Answered a question in a class', 'classes'),
    'class.question_closed': a('Closed a question', 'classes'),
    'class.question_deleted': a('Deleted a question', 'classes'),
    'class.question_pushed': a('Put a question to the room', 'classes'),
    'class.question_withdrawn': a('Withdrew a question', 'classes'),
    'class.reschedule': a('Moved a class', 'classes'),
    'class.room_settings': a('Changed a class room’s settings', 'classes'),
    'class.start': a('Started a class', 'classes'),
    'class.unpublish': a('Took a class back to draft', 'classes'),
    'class.update': a('Changed a class', 'classes'),
    // --- events -------------------------------------------------------------------------
    'event.archive': a('Archived an event', 'events'),
    'event.cancel': a('Called off an event', 'events'),
    'event.create': a('Added an event', 'events'),
    'event.link_opened': a('Opened an event’s link', 'events'),
    'event.publish': a('Published an event', 'events'),
    'event.reordered': a('Changed the order of events', 'events'),
    'event.unpublish': a('Took an event back to draft', 'events'),
    'event.update': a('Changed an event', 'events'),
    'saved.added': a('Saved something for later', 'events'),
    'saved.removed': a('Removed something they had saved', 'events'),
    // --- payments -----------------------------------------------------------------------
    'payment.abandoned': a('Stopped paying part-way', 'payments'),
    'payment.confirmed': a('Confirmed a payment', 'payments'),
    'payment.failed': a('Card payment refused', 'payments'),
    'payment.intent_created': a('Started paying', 'payments'),
    'payment.refund_retried': a('Sent a failed refund again', 'payments'),
    'payment.refunded': a('Refunded a payment', 'payments'),
    'payment.succeeded': a('Card payment went through', 'payments'),
    'payment_method.attached': a('Added a card', 'payments'),
    'payment_method.default_set': a('Chose their default card', 'payments'),
    'payment_method.detached': a('Removed a card', 'payments'),
    'payout.destination_set': a('Changed where their money is paid', 'payments'),
    // --- professionals' listings ----------------------------------------------------------
    'calendar.connected': a('Connected their calendar', 'listings'),
    'calendar.synced': a('Refreshed their connected calendar', 'listings'),
    'category.interest_registered': a('Asked to hear when a category opens', 'listings'),
    'provider.availability_settings': a('Changed how far ahead they can be booked', 'listings'),
    'provider.calendar_disconnected': a('Disconnected their calendar', 'listings'),
    'provider.create': a('Invited a professional', 'listings'),
    'provider.document_added': a('Added a document to their sign-up', 'listings'),
    'provider.document_removed': a('Removed a document from their sign-up', 'listings'),
    'provider.edit_profile': a('Changed a professional’s profile', 'listings'),
    'provider.hard_delete': a('Deleted a professional for good', 'listings'),
    'provider.hours_changed': a('Changed their working hours', 'listings'),
    'provider.invitation_accepted': a('Accepted their invitation and set a password', 'listings'),
    'provider.invitation_requested_again': a('Asked for a new invitation link', 'listings'),
    'provider.invitation_resent': a('Sent a professional’s invitation again', 'listings'),
    'provider.member_side_opened': a('Opened the member side of the app', 'listings'),
    'provider.onboarding_decision': a('Decided on a professional’s details', 'listings'),
    'provider.onboarding_step_saved': a('Saved a step of their sign-up', 'listings'),
    'provider.onboarding_submitted': a('Sent their details for review', 'listings'),
    'provider.paid_bookings': a('Turned paid bookings on or off for a professional', 'listings'),
    'provider.paid_bookings_request_withdrawn': a('Withdrew their request for paid bookings', 'listings'),
    'provider.paid_bookings_requested': a('Asked to take paid bookings', 'listings'),
    'provider.payment_settings': a('Changed payment settings', 'listings'),
    'provider.profile_changed': a('Changed their profile', 'listings'),
    'provider.service_bookable': a('Turned a service on or off', 'listings'),
    'provider.service_payment': a('Changed how a service is paid for', 'listings'),
    'provider.services_changed': a('Changed their services', 'listings'),
    'provider.status': a('Paused or restored a listing', 'listings'),
    'provider.time_off_added': a('Changed their hours for particular days', 'listings'),
    'provider.verify_registration': a('Recorded a registration as checked', 'listings'),
    'review.changed': a('Changed their review of a professional', 'listings'),
    'review.written': a('Reviewed a professional', 'listings'),
    // --- support and reports --------------------------------------------------------------
    'app_request.marked': a('Marked an app download link as sent', 'support'),
    'app_request.received': a('Asked for the app’s download link', 'support'),
    'content.reported': a('Reported something', 'support'),
    'content_report.resolved': a('Dealt with a report', 'support'),
    'support.message_sent': a('Wrote to support', 'support'),
    'support_message.answered': a('Answered a support message', 'support'),
    // --- settings and content -------------------------------------------------------------
    'activity.create': a('Added a mindfulness activity', 'settings'),
    'activity.update': a('Changed a mindfulness activity', 'settings'),
    'category.active': a('Turned a category on or off', 'settings'),
    'category.create': a('Added a category', 'settings'),
    'category.delete': a('Removed a category', 'settings'),
    'category.featured_limit': a('Changed how many are featured in a category', 'settings'),
    'category.update': a('Changed a category', 'settings'),
    'city.area_added': a('Added an area to a city', 'settings'),
    'city.area_removed': a('Removed an area from a city', 'settings'),
    'city.create': a('Added a city', 'settings'),
    'city.interest_registered': a('Asked to hear when the app reaches their city', 'settings'),
    'city.status': a('Turned a city on or off', 'settings'),
    'city.update': a('Changed a city', 'settings'),
    'export.downloaded': a('Downloaded a table', 'team'),
    'help_article.create': a('Added a help article', 'settings'),
    'help_article.delete': a('Removed a help article', 'settings'),
    'help_article.update': a('Changed a help article', 'settings'),
    'legal.published': a('Published a legal document', 'settings'),
    /* Mood tags went on 2026-09-25 (mood is a fixed five). Kept so entries already in
       the log still read as words. */
    'mood_tag.create': a('Added a mood tag', 'settings'),
    'mood_tag.update': a('Changed a mood tag', 'settings'),
    'notification_type.update': a('Changed a notification’s defaults', 'settings'),
    'platform_setting.changed': a('Changed a platform setting', 'settings'),
    'quote.create': a('Added a quote', 'settings'),
    'quote.update': a('Changed a quote', 'settings'),
    'specialty.active': a('Turned a specialty on or off', 'settings'),
    'specialty.create': a('Added a specialty', 'settings'),
    'specialty.delete': a('Removed a specialty', 'settings'),
    'specialty.update': a('Changed a specialty', 'settings'),
    // --- health records -------------------------------------------------------------------
    'health.break_glass': a('Opened somebody’s health record', 'health'),
    // --- the platform ---------------------------------------------------------------------
    'platform.alert': a('Raised an alert for the team', 'platform'),
    'platform.job_failed': a('A background task gave up after several tries', 'platform'),
    /** Any request the server could not finish (a 5xx), on a route that records nothing else. */
    'request.failed': a('Something went wrong on the server', 'platform'),
};
/** The area an action files under; `platform` for one the list does not know. */
export function areaOf(action) {
    return ACTIVITY[action]?.area ?? 'platform';
}
/** The kind of record an entry is about, in words, for when it has no name of its own. */
export const ACTIVITY_SUBJECT_WORDS = {
    account: 'an account',
    admin_role: 'a role',
    app_request: 'a download request',
    booking: 'an appointment',
    category: 'a category',
    city: 'a city',
    content_report: 'a report',
    enrollment: 'a place in a class',
    event: 'an event',
    export: 'an export',
    help_article: 'a help article',
    legal_document: 'a legal document',
    masterclass: 'a class',
    mindfulness_activity: 'an activity',
    mood_tag: 'a mood tag',
    notification_type: 'a notification',
    payment_intent: 'a payment',
    payment_method: 'a card',
    platform_setting: 'a setting',
    provider: 'a professional',
    quote: 'a quote',
    request: 'a request',
    saved_item: 'a saved item',
    support_message: 'a support message',
};
//# sourceMappingURL=activity.js.map